import { useEffect, useMemo, useRef, useState } from 'react';

import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { createLoginUrl } from '@/apiCalls/createLoginUrl';
import { submitApplication } from '@/apiCalls/submitApplication';
import { DisplayComponent } from '@/components/DisplayComponent';
import { DisplayHeading } from '@/components/DisplayHeading';
import { DocumentUpload } from '@/components/DocumentUpload';
import { Header } from '@/components/Header';
import { ProgressBar } from '@/components/ProgressBar';
import {
    ESupportingDocCodes,
    selectFirstName,
    selectSelectedLender,
    selectSupportingDocs,
    setProgressPercent,
} from '@/context/borrower';
import {
    EDisplays,
    selectMandatorySupportingDocuments,
    selectPartnerName,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { selectIsLoggedIn, selectLoginUrl, setLoginUrl } from '@/context/user';
import { useScreenCopy } from '@/copy';
import { TRUCKSTOP_PARTNER_NAME } from '@/partners/truckstop';
import { getQrPushLocation } from '@/utils/getQrPushLocation';
import { mergeRequiredDocuments } from '@/utils/getRequiredDocuments';
import { EConversionType, reportConversion } from '@/utils/reportConversion';
import { SetFinalScreen } from '@/utils/SetFinalScreen';

import { SupportingDocsButtons } from './SupportingDocsButtons';

export const testId = 'screen-SupportingDocs';

export function SupportingDocs(): JSX.Element {
    const baseId = 'screen-SupportingDocs';
    // ***** Local State *****
    const [showErrors, setShowErrors] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    // Guards the auto-submit below. SetFinalScreen usually only opens a modal,
    // so this component stays mounted afterwards — without the ref, any
    // re-render would submit the application again.
    const hasAutoSubmitted = useRef(false);

    // ***** Redux State *****
    const screenCopy = useScreenCopy('supportingDocs');
    const supportingDocs = useAppSelector(selectSupportingDocs);
    const selectedLender = useAppSelector(selectSelectedLender);
    const loginUrl = useAppSelector(selectLoginUrl);
    const firstName = useAppSelector(selectFirstName);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);
    const mandatoryDocs = useAppSelector(selectMandatorySupportingDocuments);
    const dispatch = useAppDispatch();
    const partnerName = useAppSelector(selectPartnerName);
    const isTruckstop = partnerName === TRUCKSTOP_PARTNER_NAME;

    // The lender's documents plus any the brand always enforces. Undefined when
    // there is genuinely nothing to collect.
    const requiredDocs = useMemo(
        () =>
            mergeRequiredDocuments(
                selectedLender?.requiredDocuments,
                mandatoryDocs
            ),
        [selectedLender?.requiredDocuments, mandatoryDocs]
    );

    function getExpandedIndex(): number | null {
        return null;
    }

    function documentUpload(index: number): void {
        if (expandedIndex === index) {
            setExpandedIndex(null);
            return;
        }
        setExpandedIndex(index);
    }

    // Get login url for the QR code
    useEffect(() => {
        const setUrl = (url: string): void => {
            dispatch(setLoginUrl(`${url}--${EDisplays.SUPPORTING_DOCS}`));
        };

        if (!loginUrl) {
            createLoginUrl(getQrPushLocation(), firstName)
                .then((url) => {
                    setUrl(url);
                })
                // Without this the rejection is unhandled: the QR code is a
                // convenience, so failing to build its link should not take
                // the screen down with it.
                .catch(() => undefined);
        }

        const [url, display] = loginUrl.split('--');
        if (display !== EDisplays.SUPPORTING_DOCS) {
            setUrl(url);
        }
    }, []);

    useEffect(() => {
        const number = getExpandedIndex();
        setExpandedIndex(number);
    }, [supportingDocs]);

    // Nothing to collect — neither the lender nor the brand asks for a
    // document, so there is no screen to show. Submit and move on.
    //
    // This runs in an effect rather than during render: it dispatches, calls
    // the API and navigates, none of which are safe as render side effects.
    useEffect(() => {
        if (requiredDocs) return;
        if (!isLoggedIn) return;
        if (hasAutoSubmitted.current) return;
        hasAutoSubmitted.current = true;

        dispatch(setProgressPercent(100));
        // Keep before submitApplication
        createOrUpdateBorrower();
        submitApplication();
        reportConversion(EConversionType.APPLY_TO_LENDER);

        SetFinalScreen();
    }, [requiredDocs, isLoggedIn]);

    if (!requiredDocs) {
        return <div />;
    }

    return (
        <div data-testid={testId}>
            <DisplayComponent baseId={baseId}>
                <Header />
                <ProgressBar />

                <DisplayHeading
                    title={screenCopy.title}
                    infoText={screenCopy.subtitle}
                />
                <p className='uppercase text-center text-xs font-bold'></p>
                {Object.entries(requiredDocs).map(([key, value], index) => {
                    if (
                        key === ESupportingDocCodes.BANK_STATEMENTS_12M ||
                        key === ESupportingDocCodes.BANK_STATEMENTS_3M ||
                        key === ESupportingDocCodes.BANK_STATEMENTS_6M
                    ) {
                        // return <UploadBankStatements key={key} code={key} />;
                        return;
                    } else if (
                        key === ESupportingDocCodes.PRIVACY ||
                        key === ESupportingDocCodes.LENDER_SPECIFIC_PRIVACY ||
                        key === ESupportingDocCodes.RATES_TITLE_SEARCH ||
                        key === ESupportingDocCodes.LENDER_SPECIFIC_PRIVACY
                    ) {
                        return;
                    }

                    // Truckstop only needs the driver's license
                    if (
                        isTruckstop &&
                        key !== ESupportingDocCodes.DRIVERS_LICENSE_BACK &&
                        key !== ESupportingDocCodes.DRIVERS_LICENSE_FRONT
                    ) {
                        return;
                    }

                    {
                        return (
                            <DocumentUpload
                                key={key}
                                baseId={baseId}
                                showErrors={
                                    showErrors && !supportingDocs[key]?.length
                                }
                                code={key}
                                document={supportingDocs[key]}
                                value={value}
                                expanded={expandedIndex === index}
                                handleExpanded={(): void =>
                                    documentUpload(index)
                                }
                            />
                        );
                    }
                })}
                <SupportingDocsButtons
                    baseId={baseId}
                    setShowErrors={setShowErrors}
                />
            </DisplayComponent>
        </div>
    );
}
